import z from "zod";

import { BUILDER_PAGE_TYPES } from "./libs/builder/schema";
import { parseHardGateConfig } from "./libs/hardGate/parseHardGateConfig";
import { resolveAuthAppRouterEnabled } from "./utils/resolveAuthAppRouterEnabled/resolveAuthAppRouterEnabled";
import {
  checkIsGeographicalAreaFilteringEnabled,
  parseGeographicalAreaFilteringConfig,
} from "./utils/resolveGeographicalAreaFiltering/resolveGeographicalAreaFiltering";

export type RewriteConfig = z.infer<typeof REWRITE_CONFIG_SCHEMA>;

export const REWRITE_CONFIG_SCHEMA = z.object({
  shop: z.string().optional(),
  "shop-geographical-area": z.string().optional(),
  "loyalty-blog": z.string().optional(),
  blog: z.string().optional(),
  faq: z.string().optional(),
  "free-trial": z.string().optional(),
  checkout: z.string().optional(),
  auth: z.string().optional(),
  profile: z.string().optional(),
  "nested-store-locator": z.string().optional(),
});

export const REWRITE_LOCALE_CONFIG_SCHEMA = z.record(z.string(), REWRITE_CONFIG_SCHEMA);
export const prepareRewriteUrls = (envRewriteConfig?: string) => {
  const rewriteUrls = JSON.parse(envRewriteConfig || "{}");
  const rewriteConfig = REWRITE_LOCALE_CONFIG_SCHEMA.safeParse(rewriteUrls);
  const isValidConfig = rewriteConfig.success && rewriteConfig.data;
  if (!isValidConfig) {
    return {};
  }
  return rewriteConfig.data;
};

const MAGENTO_PAGE_TYPES = z.enum(["products", "categories", "shop", "shop-subcategories"]);

const FILTERING_KEY_SCHEMA = z.enum(["delivery_region_code", "province"]);

const geographicalAreaFilteringConfig = parseGeographicalAreaFilteringConfig(
  process.env.NEXT_PUBLIC_GEOGRAPHICAL_AREA_FILTERING_ENABLED
);

export const env = {
  builderApiKey: process.env.NEXT_PUBLIC_BUILDER_API_KEY ?? "",
  builderGraphqlEndpoint: process.env.NEXT_PUBLIC_BUILDER_GRAPHQL_ENDPOINT ?? "",
  magentoGraphqlEndpoint: process.env.NEXT_PUBLIC_MAGENTO_GRAPHQL_ENDPOINT ?? "",
  brand: process.env.NEXT_PUBLIC_BRAND ?? "ploom",
  devDebugFetchCache: process.env.NEXT_PUBLIC_DEV_DEBUG_FETCH_CACHE ?? "",
  fetchCacheEnable: process.env.NEXT_PUBLIC_FETCH_CACHE_ENABLE ?? "",
  siteUrl: process.env.NEXT_PUBLIC_SITE_URL ?? "",
  rewriteUrlsConfig: prepareRewriteUrls(process.env.NEXT_PUBLIC_REWRITE_CONFIG),
  specificUrlsRewriteConfig: prepareRewriteUrls(process.env.NEXT_PUBLIC_SPECIFIC_URLS_REWRITE_CONFIG),
  magentoRootCategoryUid: process.env.NEXT_PUBLIC_ROOT_CATEGORY_UID ?? "",
  availableMagentoStores: process.env.NEXT_PUBLIC_AVAILABLE_MAGENTO_STORES ?? "",
  ssgCacheEnable: process.env.NEXT_PUBLIC_SSG_CACHE_ENABLE === "1",
  hardGateConfig: parseHardGateConfig(process.env.NEXT_PUBLIC_HARD_GATE_CONFIG),
  prerenderMagentoPagesGroups: z
    .array(MAGENTO_PAGE_TYPES)
    .parse(process.env.PRERENDER_MAGENTO_PAGE_GROUPS?.split(",").filter(Boolean) || []),
  prerenderBuilderPageGroups: z
    .array(BUILDER_PAGE_TYPES)
    .parse(process.env.PRERENDER_BUILDER_PAGE_GROUPS?.split(",").filter(Boolean) || []),
  siteMapBuilderPageOnly: process.env.NEXT_PUBLIC_SITEMAP_BUILDER_PAGE_ONLY === "1",
  isAuthAppRouterEnabled: resolveAuthAppRouterEnabled(process.env.NEXT_PUBLIC_AUTH_APP_ROUTER_ENABLED),
  getIsGeographicalAreaFilteringEnabled: (locale: string) =>
    checkIsGeographicalAreaFilteringEnabled(geographicalAreaFilteringConfig, locale),
  filteringKey: FILTERING_KEY_SCHEMA.catch("delivery_region_code").parse(process.env.NEXT_PUBLIC_FILTERING_KEY),
} as const;
